import React from 'react';

import { STRANDS_COLORS, STRANDS_CFG } from '../../../../../theme/tokens';
import { STRANDS_LIGHT } from './StrandsField.tokens';
import { Canvas } from './StrandsField.styles';

/** What the strands are drawn over. */
export type StrandsSurface = 'dark' | 'light';

/**
 * The reactbits "Strands" animation, ported to a raw WebGL2 fragment shader (no GL
 * library, glass-ball pass omitted since it is off). Flowing brand-coloured strands
 * with glow. Renders only while `active` (the morph's strands phase) and pauses
 * otherwise / when the tab is hidden, so it costs nothing as a logo most of the time.
 * Reduced motion stays the caller's call: keep it inactive (LumioMorph) or unmounted.
 */
export type StrandsFieldProps = {
  /** Run the rAF loop (the morph turns this on only during the strands phase). */
  active: boolean;
  /** Shader zoom (default STRANDS_CFG.scale); read once when the GL context is set up. A caller that draws on a
   *  larger canvas for headroom divides it by the same factor to keep the strands the same on-screen size. */
  scale?: number;
  /** The surface under the canvas (default 'dark': the deployment strip's look, additive light whose alpha follows
   *  its luminance). 'light' is for a light, frosted surface, where that light would fade away: each strand is laid
   *  down like ink (normal alpha, colours deepened with their hue kept) over a soft halo of its own bright colour.
   *  Same motion, same palette; set up with the GL context, like `scale`. */
  surface?: StrandsSurface;
  className?: string;
};

const MAX_COLORS = 8;
const MAX_STRANDS = 12;

const VERT = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}`;

/** Uniforms and palette helpers, shared by both surfaces. */
const FRAG_HEAD = `#version 300 es
precision highp float;

uniform float uTime;
uniform vec2 uResolution;
uniform vec3 uColors[${MAX_COLORS}];
uniform int uColorCount;
uniform int uStrandCount;
uniform float uSpeed;
uniform float uAmplitude;
uniform float uWaviness;
uniform float uThickness;
uniform float uGlow;
uniform float uTaper;
uniform float uSpread;
uniform float uHueShift;
uniform float uIntensity;
uniform float uOpacity;
uniform float uScale;
uniform float uSaturation;

out vec4 fragColor;

const float PI = 3.14159265;

vec3 spectrum(float t) {
  return 0.5 + 0.5 * cos(2.0 * PI * (t + vec3(0.00, 0.33, 0.67)));
}

vec3 samplePalette(float t) {
  t = fract(t);
  float scaled = t * float(uColorCount);
  int idx = int(floor(scaled));
  float blend = fract(scaled);
  int nextIdx = idx + 1;
  if (nextIdx >= uColorCount) nextIdx = 0;
  return mix(uColors[idx], uColors[nextIdx], blend);
}

vec3 strandColor(float t) {
  if (uColorCount > 0) return samplePalette(t);
  return spectrum(t);
}

`;

/** The field's coordinates and its envelope (it fades the strands out at both ends). */
const FIELD = `  vec2 uv = (gl_FragCoord.xy - 0.5 * uResolution) / uResolution.y;
  uv /= max(uScale, 0.0001);

  float e = 0.06 + uIntensity * 0.94;
  float env = pow(max(cos(uv.x * PI * 1.3), 0.0), uTaper);`;

/** Strand i's wave, its soft line profile g and its hue h: the motion, shared by both surfaces. */
const STRAND = `    float fi = float(i);
    float ph = fi * 1.7 * uSpread;
    float freq = (2.0 + fi * 0.35) * uWaviness;
    float spd = 1.4 + fi * 1.2;

    float tt = uTime * uSpeed;
    float w = sin(uv.x * freq + tt * spd + ph) * 0.60
            + sin(uv.x * freq * 1.1 - tt * spd * 0.7 + ph * 1.7) * 0.40;

    float amp = (0.1 + 0.02 * e) * env * uAmplitude;
    float y = w * amp;

    float d = abs(uv.y - y);
    float thick = (0.001 + 0.05 * e) * (0.35 + env) * uThickness;
    float g = thick / (d + thick * 0.45);
    g = g * g;

    float h = fi / float(uStrandCount) + uv.x * 0.30 + uTime * 0.04 + uHueShift;`;

/** Dark surface (the original pass, unchanged): additive light, tone-mapped, its alpha = its luminance. */
const FRAG_DARK = `${FRAG_HEAD}void main() {
${FIELD}

  vec3 col = vec3(0.0);

  for (int i = 0; i < ${MAX_STRANDS}; i++) {
    if (i >= uStrandCount) break;

${STRAND}
    col += strandColor(h) * g * env;
  }

  col *= 0.45 + 0.7 * e;
  col = 1.0 - exp(-col * uGlow);

  float gray = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = max(mix(vec3(gray), col, uSaturation), 0.0);

  float lum = max(max(col.r, col.g), col.b);
  float alpha = clamp(lum, 0.0, 1.0) * uOpacity;

  fragColor = vec4(col * uOpacity, alpha);
}`;

/** Light surface: each strand laid down like ink (normal alpha) over a soft halo of its own bright colour. */
const FRAG_LIGHT = `${FRAG_HEAD}uniform vec3 uBridge;
uniform float uInkLuma;
uniform float uBody;
uniform float uHaloAlpha;
uniform float uHaloReach;

const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

// The palette as the dark pass samples it, except its wrap (last colour back to the first): cyan blended straight
// into orange is grey mud on white, so the wrap passes through the bridge colour.
vec3 lightColor(float t) {
  if (uColorCount <= 0) return spectrum(t);
  t = fract(t);
  float scaled = t * float(uColorCount);
  int idx = int(floor(scaled));
  float blend = fract(scaled);
  if (idx < uColorCount - 1) return mix(uColors[idx], uColors[idx + 1], blend);
  if (blend < 0.5) return mix(uColors[idx], uBridge, blend * 2.0);
  return mix(uBridge, uColors[0], blend * 2.0 - 1.0);
}

void main() {
${FIELD}
  // The dark pass's intensity and envelope, applied to each strand's profile.
  float boost = (0.45 + 0.7 * e) * env;

  vec4 acc = vec4(0.0);

  for (int i = 0; i < ${MAX_STRANDS}; i++) {
    if (i >= uStrandCount) break;

${STRAND}
    // The bright colour lights the halo; the body takes it as ink, deepened to uInkLuma with its hue kept.
    vec3 tint = lightColor(h);
    vec3 ink = tint * min(1.0, uInkLuma / max(dot(tint, LUMA), 0.001));

    // Cubed, the profile draws a crisp line; squared, a halo that hugs it (no haze spread over the surface).
    float p = g * boost;
    float b = p * uBody;
    float body = 1.0 - exp(-b * b * b);
    float r = p * uHaloReach;
    float halo = uHaloAlpha * (1.0 - exp(-r * r)) * (1.0 - body);

    // Premultiplied: the body over its halo, the strand over the ones before it.
    vec4 strand = vec4(ink * body + tint * halo, body + halo);
    acc = strand + acc * (1.0 - strand.a);
  }

  fragColor = acc * uOpacity;
}`;

/** Both passes output premultiplied colour, so they share the same context attributes. */
const FRAG: Record<StrandsSurface, string> = { dark: FRAG_DARK, light: FRAG_LIGHT };

/** "#RRGGBB" → [r, g, b] in 0..1. */
const hexToRgb = (hex: string): [number, number, number] => {
  const n = parseInt(hex.replace('#', ''), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

const compileShader = (gl: WebGL2RenderingContext, type: number, source: string): WebGLShader | null => {
  const shader = gl.createShader(type);
  if (!shader) return null;
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    gl.deleteShader(shader);
    return null;
  }
  return shader;
};

/** Static uniforms (set once on the program in use): the morph never changes them mid-flight. A uniform the
 *  surface's pass does not use has a null location, which WebGL ignores. */
const setStaticUniforms = (gl: WebGL2RenderingContext, program: WebGLProgram, scale: number): void => {
  const u = (name: string): WebGLUniformLocation | null => gl.getUniformLocation(program, name);
  const padded: number[] = [];
  for (let i = 0; i < MAX_COLORS; i++) {
    const hex = STRANDS_COLORS[i] ?? STRANDS_COLORS[STRANDS_COLORS.length - 1];
    padded.push(...hexToRgb(hex));
  }
  gl.uniform3fv(u('uColors'), new Float32Array(padded));
  gl.uniform1i(u('uColorCount'), STRANDS_COLORS.length);
  gl.uniform1i(u('uStrandCount'), Math.min(STRANDS_CFG.count, MAX_STRANDS));
  gl.uniform1f(u('uSpeed'), STRANDS_CFG.speed);
  gl.uniform1f(u('uAmplitude'), STRANDS_CFG.amplitude);
  gl.uniform1f(u('uWaviness'), STRANDS_CFG.waviness);
  gl.uniform1f(u('uThickness'), STRANDS_CFG.thickness);
  gl.uniform1f(u('uGlow'), STRANDS_CFG.glow);
  gl.uniform1f(u('uTaper'), STRANDS_CFG.taper);
  gl.uniform1f(u('uSpread'), STRANDS_CFG.spread);
  gl.uniform1f(u('uHueShift'), STRANDS_CFG.hueShift);
  gl.uniform1f(u('uIntensity'), STRANDS_CFG.intensity);
  gl.uniform1f(u('uOpacity'), STRANDS_CFG.opacity);
  gl.uniform1f(u('uScale'), scale);
  gl.uniform1f(u('uSaturation'), STRANDS_CFG.saturation);

  const { from, to, at } = STRANDS_LIGHT.bridge;
  const [fromR, fromG, fromB] = hexToRgb(STRANDS_COLORS[from]);
  const [toR, toG, toB] = hexToRgb(STRANDS_COLORS[to]);
  gl.uniform3f(u('uBridge'), fromR + (toR - fromR) * at, fromG + (toG - fromG) * at, fromB + (toB - fromB) * at);
  gl.uniform1f(u('uInkLuma'), STRANDS_LIGHT.inkLuma);
  gl.uniform1f(u('uBody'), STRANDS_LIGHT.body);
  gl.uniform1f(u('uHaloAlpha'), STRANDS_LIGHT.haloAlpha);
  gl.uniform1f(u('uHaloReach'), STRANDS_LIGHT.haloReach);
};

export const StrandsField = ({
  active,
  scale = STRANDS_CFG.scale,
  surface = 'dark',
  className,
}: StrandsFieldProps): React.ReactElement => {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const activeRef = React.useRef(active);
  const startRef = React.useRef<(() => void) | null>(null);
  const stopRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: true });
    if (!gl) return;

    const vert = compileShader(gl, gl.VERTEX_SHADER, VERT);
    const frag = compileShader(gl, gl.FRAGMENT_SHADER, FRAG[surface]);
    const program = gl.createProgram();
    if (!vert || !frag || !program) return;
    gl.attachShader(program, vert);
    gl.attachShader(program, frag);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return;
    gl.useProgram(program);

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(program, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const uTime = gl.getUniformLocation(program, 'uTime');
    const uResolution = gl.getUniformLocation(program, 'uResolution');
    setStaticUniforms(gl, program, scale);

    // `force` on setup: a re-run (a new scale or surface, or StrictMode) gets the same, already sized context but a
    // fresh program, whose uResolution still has to be set.
    const resize = (force: boolean): void => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (!force && canvas.width === w && canvas.height === h) return;
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(uResolution, w, h);
    };
    resize(true);
    const observer = new ResizeObserver(() => resize(false));
    observer.observe(canvas);

    let raf = 0;
    let running = false;
    const loop = (t: number): void => {
      gl.uniform1f(uTime, t / 1000);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(loop);
    };
    const start = (): void => {
      if (running || document.hidden) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const stop = (): void => {
      running = false;
      cancelAnimationFrame(raf);
    };
    startRef.current = start;
    stopRef.current = stop;

    // Back on the tab it resumes if still wanted (a caller that keeps `active` on would otherwise stay frozen).
    const onVisibility = (): void => {
      if (document.hidden) stop();
      else if (activeRef.current) start();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // A re-run while active resumes at once: the `active` effect below only runs when `active` changes.
    if (activeRef.current) start();

    return () => {
      stop();
      startRef.current = null;
      stopRef.current = null;
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      gl.deleteBuffer(buffer);
      gl.deleteShader(vert);
      gl.deleteShader(frag);
      gl.deleteProgram(program);
    };
  }, [scale, surface]);

  React.useEffect(() => {
    activeRef.current = active;
    if (active) startRef.current?.();
    else stopRef.current?.();
  }, [active]);

  return <Canvas ref={canvasRef} className={className} aria-hidden="true" />;
};

export default StrandsField;
